/**
 * True when `src` is a provider-generated *placeholder* avatar rather than a
 * real uploaded photo — currently just Clerk's default silhouette.
 *
 * Why this lives in the Avatar: our data layer (study-group member rows,
 * leaderboard) persists Clerk's `imageUrl`, which is a generated silhouette
 * when the user never uploaded a photo. The authoritative signal is Clerk's
 * `hasImage` flag (gated server-side), but existing rows and any un-gated path
 * still leak the placeholder URL. Treating it as "no image" here lets the
 * Avatar fall back to its per-name initials chip everywhere, immediately —
 * no re-sync/heal required.
 *
 * Clerk serves avatars from `img.clerk.com/<base64-json>`, and GENERATES two
 * kinds when the user never uploaded anything:
 *   - `{"type":"default",…}`  — the grey silhouette
 *   - `{"type":"initials",…}` — a coloured circle with the user's first letter
 * Their base64 always begins `eyJ0eXBlIjoiZGVmYXVs` (== `{"type":"defaul`) and
 * `eyJ0eXBlIjoiaW5pdGlh` (== `{"type":"initia`) respectively. Uploaded photos
 * encode `type:"proxy"`/`"user"`, so they don't match either prefix.
 *
 * The `initials` kind is the subtle one, and it is why this guard exists at all
 * rather than just trusting `hasImage`: Clerk's generated initials avatar LOOKS
 * like a real avatar, so it renders happily and quietly replaces the DS chip —
 * the Profile hero showed a vermilion Clerk circle while every other surface
 * showed the account's amber DS chip for the same user. A generated image is
 * not a photo; it's the provider's guess at the same fallback we already draw,
 * in a palette that isn't ours.
 *
 * ponytail: base64-prefix match, not a full decode — if Clerk ever reorders
 * the payload keys this silently stops matching and we render the image (the
 * pre-guard behaviour, no worse). The server-side `hasImage` gate is the real
 * data cleanup; this is the display-layer safety net. Upgrade path: decode the
 * payload and check `type` if Clerk's format drifts.
 */

/** Base64 heads of the Clerk payloads that mean "we generated this". */
const CLERK_GENERATED_PREFIXES = [
  'eyJ0eXBlIjoiZGVmYXVs', // {"type":"defaul…  → silhouette
  'eyJ0eXBlIjoiaW5pdGlh', // {"type":"initia…  → coloured initial
] as const;

export function isPlaceholderAvatarSrc(src: string | undefined): boolean {
  if (!src) return false;
  if (!src.includes('img.clerk.com')) return false;
  return CLERK_GENERATED_PREFIXES.some((prefix) => src.includes(prefix));
}
